import { VStack, HStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Text, Box } from 'native-base';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, { useAnimatedStyle, useSharedValue, runOnJS } from 'react-native-reanimated';

interface MultiSliderProps {
  min: number;
  max: number;
  steps: number;
  title: string;
  onValueChange: (range: RangeProps) => void;
  type: 'money' | 'months' | 'days';
  currentValue: { min: number; max: number };
  resetKey: boolean;
}

interface RangeProps {
  min: string;
  max: string;
}

const DEFAULT_STYLE_LINE = {
  height: 4,
  marginTop: -4,
  borderRadius: 999,
};

const MIN_DISTANCE_BETWEEN_MARKERS = 10;

export const MultiSlider: React.FC<MultiSliderProps> = ({
  currentValue,
  max,
  min,
  onValueChange,
  steps,
  title,
  type,
  resetKey,
}) => {
  const [width, setWidth] = useState(0);
  const MARKER_SIZE = 16;
  const MAX_WIDTH = width - MARKER_SIZE / 2 + 6;

  const markerLeftStartX = useRef<number>(0);
  const markerRightStartX = useRef<number>(0);
  const markerLeft = useSharedValue(1);
  const scaleMarkerLeft = useSharedValue(1);
  const markerRight = useSharedValue(1);
  const scaleMarkerRight = useSharedValue(1);

  const [range, setRange] = useState(currentValue);

  const roundValue = (value: number) =>
    Math.round((min + (value / MAX_WIDTH) * (max - min)) / steps) * steps;

  const gestureHandlerLeftMarker = Gesture.Pan()
    .onStart(() => {
      markerLeftStartX.current = markerLeft.value;
    })
    .onChange((event) => {
      scaleMarkerLeft.value = 1.3;
      const startX = markerLeftStartX.current;
      const translationX = event.translationX;
      const markerRightValue = markerRight.value;
      if (startX + translationX < 0) {
        markerLeft.value = 0;
      } else if (startX + translationX > markerRightValue - MIN_DISTANCE_BETWEEN_MARKERS) {
        markerLeft.value = markerRightValue - MIN_DISTANCE_BETWEEN_MARKERS;
      } else {
        markerLeft.value = startX + translationX;
      }
      const minValue = roundValue(markerLeft.value);
      const maxValue = roundValue(markerRight.value);
      setRange({ min: minValue, max: maxValue });
    })
    .onFinalize(() => {
      scaleMarkerLeft.value = 1;
      const minValue = roundValue(markerLeft.value);
      const maxValue = roundValue(markerRight.value);
      runOnJS(onValueChange)({ min: minValue.toString(), max: maxValue.toString() });
    });

  const gestureHandlerRightMarker = Gesture.Pan()
    .onStart(() => {
      markerRightStartX.current = markerRight.value;
    })
    .onChange((event) => {
      scaleMarkerRight.value = 1.3;

      const startX = markerRightStartX.current;
      const translationX = event.translationX;
      const markerLeftValue = markerLeft.value;

      if (startX + translationX > MAX_WIDTH) {
        markerRight.value = MAX_WIDTH;
      } else if (startX + translationX < markerLeftValue + MIN_DISTANCE_BETWEEN_MARKERS) {
        markerRight.value = markerLeftValue + MIN_DISTANCE_BETWEEN_MARKERS;
      } else {
        markerRight.value = startX + translationX;
      }

      const minValue = roundValue(markerLeft.value);
      const maxValue = roundValue(markerRight.value);
      setRange({ min: minValue, max: maxValue });
    })
    .onFinalize(() => {
      scaleMarkerRight.value = 1;

      const minValue = roundValue(markerLeft.value);
      const maxValue = roundValue(markerRight.value);
      runOnJS(onValueChange)({ min: minValue.toString(), max: maxValue.toString() });
    });

  const styles = StyleSheet.create({
    marker: {
      position: 'absolute',
      height: MARKER_SIZE,
      width: MARKER_SIZE,
      borderRadius: MARKER_SIZE / 2,
      backgroundColor: Utils.ColorToken.WithOpacity('brand.tertiary.principal', 0.5),
      marginLeft: -8,
      marginTop: -MARKER_SIZE + 6,
    },
  });

  const styleLine = useAnimatedStyle(() => {
    return {
      ...DEFAULT_STYLE_LINE,
      backgroundColor: Utils.ColorToken.WithOpacity('brand.tertiary.principal', 0.5),
      width: markerRight.value - markerLeft.value,
      transform: [{ translateX: markerLeft.value }],
    };
  });

  const styleBackgroundLine = useAnimatedStyle(() => {
    return {
      ...DEFAULT_STYLE_LINE,
      backgroundColor: '#00000042',
      width,
    };
  });

  const styleAnimatedLeftMarker = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateX: markerLeft.value,
        },
        {
          scale: scaleMarkerLeft.value,
        },
      ],
    };
  });

  const styleAnimatedRightMarker = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateX: markerRight.value,
        },
        {
          scale: scaleMarkerRight.value,
        },
      ],
    };
  });

  const resetSlider = useCallback(() => {
    setRange(currentValue);
    markerLeft.value = 0;
    markerRight.value = MAX_WIDTH;
  }, [MAX_WIDTH, currentValue, markerLeft, markerRight]);

  useEffect(() => {
    if (resetKey) {
      resetSlider();
    }
  }, [resetKey, resetSlider]);

  useEffect(() => {
    markerLeft.value = (currentValue.min / (max - min)) * MAX_WIDTH;
    markerRight.value = (currentValue.max / (max - min)) * MAX_WIDTH;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  const label = {
    money: (
      <HStack w="full" justifyContent={'space-between'}>
        <Text variant={'regular'} fontSize={Typography['fontsize.md']} lineHeight={'distant'}>
          {Utils.Mask.Money(range.min)}
        </Text>
        <Text variant={'regular'} fontSize={Typography['fontsize.md']} lineHeight={'distant'}>
          {range.max === max && '+ '}
          {Utils.Mask.Money(range.max)}
        </Text>
      </HStack>
    ),
    months: (
      <HStack w="full" justifyContent={'space-between'}>
        <Text variant={'regular'} fontSize={Typography['fontsize.md']} lineHeight={'distant'}>
          {range.min}
        </Text>
        <Text variant={'regular'} fontSize={Typography['fontsize.md']} lineHeight={'distant'}>
          {range.max === max && '+ '}
          {`${range.max} meses`}
        </Text>
      </HStack>
    ),
    days: (
      <HStack w="full" justifyContent={'space-between'}>
        <Text variant={'regular'} fontSize={Typography['fontsize.md']} lineHeight={'distant'}>
          {range.min}
        </Text>
        <Text variant={'regular'} fontSize={Typography['fontsize.md']} lineHeight={'distant'}>
          {range.max === max && '+ '}
          {`${range.max} dias`}
        </Text>
      </HStack>
    ),
  };

  return (
    <VStack w={'full'} onLayout={(event) => setWidth(event.nativeEvent.layout.width)}>
      <Text variant="semibold" fontSize={Typography['fontsize.md']} lineHeight="distant">
        {title}
      </Text>
      <Box w={'full'} py={Spacing['inline.xxs']}>
        <Animated.View style={styleBackgroundLine} />
        <Animated.View style={styleLine} />
        <Box>
          <GestureDetector gesture={gestureHandlerLeftMarker}>
            <Animated.View style={[styles.marker, styleAnimatedLeftMarker]} />
          </GestureDetector>
          <GestureDetector gesture={gestureHandlerRightMarker}>
            <Animated.View style={[styles.marker, styleAnimatedRightMarker]} />
          </GestureDetector>
        </Box>
      </Box>
      {label[type]}
    </VStack>
  );
};
